import { FORM_FIELDS_MAP, FORM_FIELDS_MAP_FOR_HA, SOURCES } from 'features/new-candidate/constants';
import {
  formatPhoneNumbers,
  PHONE_FORMAT_MODES,
  PHONE_ROW_PREFIX
} from 'UI/components/organisms/PhoneNumbersForm/utils';
import { ContactEntity, ContactRole, HiringAuthorityEntity } from 'UI/constants/entityTypes';
import { getId } from 'UI/utils';
import { preloadFromBackend } from 'UI/utils/forms';

export const formatResults = ({ contactId, fromSource, hiringId, id, isFromDraft, results }) => {
  if (!results) return null;

  if (isFromDraft)
    return {
      ...(results?.draft ?? {}),
      phones: formatPhoneNumbers(results?.draft, PHONE_FORMAT_MODES.preload),
      isFromDraft,
      draftId: id,
      draftUpdatedAt: results?.updatedAt,
      legacyDraftId: results?.legacyId
    };

  const isFromTalentPool = fromSource === SOURCES.FROM_TALENT_POOL;
  const isFromResume = fromSource === SOURCES.FROM_RESUME;

  if (isFromTalentPool || isFromResume) {
    const formattedResults = formatApplicantData({
      id,
      results,
      fromSource,
      isFromResume,
      isFromTalentPool
    });
    return formattedResults;
  }

  if (contactId) {
    const { company_id, current_company } = results;
    const parsedCompanyId = Number(company_id);
    const hasValidCompanyId =
      company_id !== null &&
      company_id !== undefined &&
      company_id !== '' &&
      Number.isFinite(parsedCompanyId);
    const companyFromContact =
      hasValidCompanyId && current_company ? { id: parsedCompanyId, name: current_company } : null;

    const formattedResults = preloadFromBackend(
      {
        ...results,
        company: companyFromContact,
        [FORM_FIELDS_MAP.FromContact.key]: {
          id,
          role_id: ContactRole.Name,
          type: ContactEntity.id
        }
      },
      FORM_FIELDS_MAP
    );
    return { ...formattedResults, contactId, fromSource };
  }

  if (hiringId) {
    const formattedResults = preloadFromBackend(
      {
        ...results,
        company: results?.company,
        [FORM_FIELDS_MAP_FOR_HA.FromContact.key]: {
          id,
          role_id: ContactRole.HiringAuthority,
          type: HiringAuthorityEntity.id
        }
      },
      FORM_FIELDS_MAP_FOR_HA
    );

    return { ...formattedResults, hiringId, fromSource };
  }

  return results;
};

const formatApplicantData = ({ id, results, isFromResume, isFromTalentPool, fromSource }) => {
  const {
    city,
    companyId,
    companyName,
    contactId,
    dataSheetId,
    email,
    firstName,
    hiringId,
    keywords,
    lastName,
    nickname,
    otherEmail,
    phones,
    position,
    source,
    sourceUrl,
    specialty,
    state,
    subspecialty,
    title,
    zipCode
  } = results || {};

  const formData = {
    attachments: formatAttachments(results?.attachments),
    applicantId: isFromTalentPool ? id : null,
    city_id: city,
    company: { id: companyId, name: companyName },
    contactId,
    dataSheet: { id: dataSheetId, progress: {} },
    dataSheetId,
    email,
    firstName,
    fromContact: buildFromContact({ contactId, hiringId }),
    fromSource,
    functionalTitle: position,
    hiringId,
    id,
    isFromResume,
    isFromTalentPool,
    keywordTags: formatKeywords(keywords),
    lastName,
    linkedinUrl: sourceUrl,
    nickname,
    personalEmail: otherEmail,
    source,
    specialty,
    state_id: state,
    subspecialty,
    title,
    zip: zipCode,
    ...transformPhoneData(phones)
  };

  return {
    ...formData,
    phones: formatPhoneNumbers(formData, PHONE_FORMAT_MODES.preload)
  };
};

const buildFromContact = ({ contactId, hiringId }) => {
  if (!contactId && !hiringId) return null;
  return hiringId
    ? { id: hiringId, type: HiringAuthorityEntity.id, role_id: ContactRole.HiringAuthority }
    : { id: contactId, type: ContactEntity.id, role_id: ContactRole.Name };
};

const formatKeywords = keywords => {
  if (!keywords || keywords?.length === 0) {
    return [];
  }
  return keywords.map(keyword => {
    return {
      keyword,
      display_text: keyword
    };
  });
};

const formatAttachments = attachments => {
  if (!attachments) return [];
  return attachments.map(item => ({ ...item, fileType: { id: item?.file_type_id } }));
};

const transformPhoneData = phonesArray => {
  const result = {
    phones: [],
    phoneDefaultId: null
  };

  if (!phonesArray) return result;

  phonesArray.forEach((phoneData, index) => {
    const phoneId = getId();

    const countryCode = {
      id: phoneData?.countryCode?.id,
      country_iso_2: phoneData?.countryCode?.country_iso_2,
      country_iso_3: phoneData?.countryCode?.country_iso_3,
      country: phoneData?.countryCode?.country,
      code: phoneData?.countryCode?.code
    };

    result.phones.push({
      id: phoneId,
      countryCode,
      phone: '',
      ext: '',
      use: '',
      type: ''
    });

    if (index === 0) {
      result.phoneDefaultId = phoneId;
    }

    result[`${PHONE_ROW_PREFIX.CountryCode}${phoneId}`] = countryCode;
    result[`${PHONE_ROW_PREFIX.Phone}${phoneId}`] = phoneData?.phone?.toString() || '';
    result[`${PHONE_ROW_PREFIX.Ext}${phoneId}`] = '';
    result[`${PHONE_ROW_PREFIX.Use}${phoneId}`] = {
      id: phoneData?.use?.id,
      title: phoneData?.use?.title
    };
    result[`${PHONE_ROW_PREFIX.Type}${phoneId}`] = {
      id: phoneData?.type?.id,
      title: phoneData?.type?.title
    };
  });

  return result;
};
